import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { App } from '@capacitor/app';
import { Capacitor, CapacitorHttp, HttpHeaders } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigurationService } from 'src/app/services/app.configuration.service';
import { AuthService } from 'src/app/services/auth.service';
import { ProfileService } from 'src/app/services/profile.service';

@Component({
  selector: 'app-mangopay-sca-validation',
  templateUrl: './mangopay-sca-validation.component.html',
  styleUrls: ['./mangopay-sca-validation.component.scss'],
})
export class MangopayScaValidationComponent implements OnInit {

  @Input() public isEnrolledSca: boolean = true;
  @Input() public hasConsentGranted: boolean = true;
  @Output() public finishEvent = new EventEmitter<any>();

  urlSafe: SafeResourceUrl;
  iframeLoaded: boolean = false;
  step: number = 0;

  text: string;
  image: number;
  btnList = [
    {
      type: 'primaryDark',
      index: 1,
      title: this.translate.instant('bid-create.continue'),
    }
  ];

  loading: boolean = false;
  private currentFlow: 'sca' | 'consent' | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private translate: TranslateService,
    private profileService: ProfileService,
    private authService: AuthService,
    private appConfigurationService: AppConfigurationService
  ) { }

  ngOnInit() {
    if (!this.isEnrolledSca) {
      this.currentFlow = 'sca';
      this.startScaFlow();
    } else if (!this.hasConsentGranted) {
      this.currentFlow = 'consent';
      this.startConsentFlow();
    } else {
      this.finish();
      return;
    }

    window.addEventListener('message', e => {
      if (e.data.func === 'control_status') {
        const status = e.data.data.value.controlStatus;
        this.step = 1;
        this.loading = true;

        if (this.currentFlow === 'sca') {
          const validated = status === 'VALIDATED';
          if (validated) {
            this.setValidatedMessageParams();
          } else {
            this.image = 1;
            this.text = this.translate.instant('SCA.ISSUE');
          }
          this.profileService.saveCategorizeResult(validated).subscribe(() => {
            this.loading = false;
            if (validated && !this.hasConsentGranted) {
              this.step = 0;
              this.iframeLoaded = false;
              this.urlSafe = null;
              this.currentFlow = 'consent';
              this.startConsentFlow();
            } else {
              this.finish();
            }
            this.cdRef.detectChanges();
          });
        } else if (this.currentFlow === 'consent') {
          if (status === 'VALIDATED') {
            this.setValidatedMessageParams();
          } else {
            this.image = 1;
            this.text = this.translate.instant('SCA.ISSUE');
          }
          this.loading = false;
          this.finish();
        }
      }
      this.cdRef.detectChanges();
    });
    this.cdRef.detectChanges();
  }

  private startScaFlow() {
    if (Capacitor.getPlatform() !== 'ios') {
      this.profileService.categorizeUser().subscribe(resp => {
        if (resp === 'Categorized') {
          if (!this.hasConsentGranted) {
            this.currentFlow = 'consent';
            this.startConsentFlow();
          } else {
            this.setValidatedMessageParams();
            this.step = 1;
          }
        } else {
          this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp);
        }
        this.cdRef.detectChanges();
      }, error => {
        console.log(error, 'error');
      });
    } else {
      this.nativeReq().then(resp => {
        if (resp === 'Categorized') {
          if (!this.hasConsentGranted) {
            this.currentFlow = 'consent';
            this.startConsentFlow();
          } else {
            this.setValidatedMessageParams();
            this.step = 1;
          }
        } else {
          this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp);
        }
        this.cdRef.detectChanges();
      }, error => {
        console.log(error, 'error');
      });
    }
  }

  private startConsentFlow() {
    if (Capacitor.getPlatform() !== 'ios') {
      this.profileService.getMangopayConsent().subscribe(resp => {
        this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp);
        this.cdRef.detectChanges();
      }, error => {
        console.log(error, 'error');
      });
    } else {
      this.nativeReqConsent().then(resp => {
        this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp);
        this.cdRef.detectChanges();
      }, error => {
        console.log(error, 'error');
      });
    }
  }

  charged(event) {
    setTimeout(() => {
      this.iframeLoaded = true;
      this.cdRef.detectChanges();
    }, 1000);
    this.cdRef.detectChanges();
  }

  setValidatedMessageParams() {
    this.image = 6;
    this.text = this.translate.instant('SCA.CAN_CONTINUE');
    this.cdRef.detectChanges();
  }

  finish() {
    this.finishEvent.emit();
    this.cdRef.detectChanges();
  }

  nativeReq() {
    return this.makeNativeTextRequest(`apiservices/profile/v1/profiles/categorize`, null);
  }

  nativeReqConsent() {
    return this.makeNativeTextRequest(`apiservices/profile/v1/profiles/mangopay/consent`, null, 'GET');
  }

  private async makeNativeTextRequest(endpoint: string, data: any, method: string = 'POST'): Promise<any> {
    try {
      const url = this.appConfigurationService.get('domain') + endpoint;

      // Obtener información de la app para el header X-Fundeen-Version
      const appInfo = await App.getInfo();
      const appVersion = `${Capacitor.getPlatform()} ${appInfo.build} ${appInfo.version}`;

      const headers: HttpHeaders = {
        'Content-Type': 'application/json',
        'X-Fundeen-Version': appVersion
      };

      // Obtener el token desde AuthService (igual que hace el interceptor)
      if (this.authService && this.authService.authData) {
        if (this.authService.authData.accessToken) {
          headers['Authorization'] = `Bearer ${this.authService.authData.accessToken}`;
        } else {
          console.error('error');
        }
      } else {
        console.error('error');
      }

      const options: any = {
        url: url,
        method: method,
        headers: headers,
        data: method === 'GET' ? undefined : (data || {}),
        // CRÍTICO: Estas opciones le dicen a iOS que NO parsee la respuesta
        readTimeout: 30000,
        connectTimeout: 30000,
        disableRedirects: false,
        // NO usar responseType aquí, en su lugar manejar la respuesta manualmente
      };

      console.log('CapacitorHttp Native Request:', { url, headers: options.headers });

      const response = await CapacitorHttp.request({
        ...options,
        // Forzar que la respuesta se lea como string
        responseType: 'text'
      });

      console.log('CapacitorHttp Native Response:', response);

      if (response.status === 401) {
        throw new Error('Unauthorized');
      }

      // La respuesta debe venir en response.data como string
      return response.data;
    } catch (error) {
      console.error('ERROR CAPACITOR HTTP:', error);
      //this.errorHandler(error);
      throw error;
    }
  }

}
